import type { GetServerSidePropsContext } from 'next';
import { get, getPrivateUrl, post } from 'utils/apiWrapper';
import { browserToSHA256 } from 'utils/browser';
import { CLOUDFRONT_MISS } from 'utils/constants/i18n';
import { getDomainValue } from 'utils/domain';
import { logError } from 'utils/error';
import { prepareHeadersFromContext, handleOriginByCookie } from 'utils/helpers';
import {
  I18N_MULTIPLE_ROUTE,
  CHECK_PROFILE_ROUTE,
  I18N_CREATE_ROUTE,
  COUNTRIES_ROUTE,
  FAQS,
  PAYMENT_METHODS_GET_ROUTE,
  SHIPPING_METHODS_GET_ROUTE,
  NEWSLETTER_SUBSCRIBE,
  RIBBON,
  NEWSLETTER_INFO_ROUTE,
  SEO,
  NEWSLETTER_UPDATE,
  NEWSLETTER_UNSUBSCRIBE,
  LOG_ROUTE,
  REGIONS_AND_CITIES,
  NEWSLETTER_PREFERENCES,
  GET_NEWSLETTER_PREFERENCES,
  SELLIGENT_POPUP,
  DONATION_BEGIN,
  DONATION_DONATE,
  I18N_LABELS,
  GLOBAL_RETURN_METHODS,
  I18N_NEW,
  TWS_EVENT_SUBSCRIBE,
  TWS_EVENT_UNSUBSCRIBE,
  TWS_EVENT_CONDITIONS,
  I18N_GET_SINGLE,
  CHILE_COMUNAS,
  ENRICHMENT_TWS,
  DEBUG,
} from 'utils/routes';
import { getUtmParams } from 'utils/utm';

import type { ApiOK } from 'types/api';

export type TGeneralActionType = {
  context?: GetServerSidePropsContext;
  profile: string;
};

export interface TGeneralActionWithBody extends TGeneralActionType {
  body: Record<string, any>;
}

type TGetTranslations = {
  context: any;
  profile: string;
  relParents: string[];
};

type TCheckProfile = {
  profile: string;
  context?: GetServerSidePropsContext;
  filters?: string;

  gender?: string;
  page?: string;
  material?: string;
};

type TProfileResponse = {
  defaultProfile: string;
  storeId: string;
  countryName: string;
  phone: string;
  whatsapp: string;
  salesOrg: string;
  global_e: boolean;
  esw: boolean;
  currency: string;
  currencySap: string;
  tax: number;
  urlTermsAndConditions: string;
  urlPrivacyPolicy: string;
  urlLegalNotice: string;
  adyenMerchant: string;
  stockInStore: boolean;
  invoiceCheck: boolean;
  taxesIncluded: boolean;
  gdpr: boolean;
  goGreenShipping: boolean;
  goGreenCarrier: string;
  freeShipping: boolean;
  freeShippingOver: number;
  shippingAdvice: string;
  newsletterAcceptByDefault: boolean;
  homePickUp: boolean;
  homePickUpPrice: number;
  config: {
    installments_payment: boolean;
    reviews: boolean;
    reviews_lang: string;
    specific_taxes: boolean;
    market_by_region: boolean;
    ia_recommendations: boolean;
    size_label: boolean;
    allow_single_char_names: boolean;
    take_back: boolean;
    voucher_pre_takeback: boolean;
    gift_card: boolean;
    allow_special_chars: boolean;
    cookiebot: boolean;
    show_free_shipping: boolean;
    show_gift_purchase: boolean;
    sku_gift_purchase: '';
    image_search: boolean;
    unbxd: boolean;
    globale_fixed_price: boolean;
    hide_telephone_numbers: boolean;
    captchaEnabled: boolean;
    captchaMinimumValue: number;
    instantFAQ: boolean;
    instantFAQ_subreason_ids: any;
    gratis_cambio_talla: boolean;
    checkout_address_autocomplete: boolean;
    cj_cid: string;
    cj_type: string;
    collectionEmptyBag: {
      W: string;
      M: string;
      K: string;
      A: string;
    };
  };
  emptyBagCollections: {
    id: string;
    title: string;
    url: string;
  }[];
  phonePrefixes: { id: string; name: string }[];
  captchaEnabled: boolean;
};

type TGetMethods = {
  context?: GetServerSidePropsContext;
  lang: string;
  countryId: string;
};

type TTwsEvent = {
  eventId: number;
  email: string;
  profile: string;
};

interface TSubscribeTwsEvent extends TTwsEvent {
  name: string;
  phone: string;
  lab: boolean;
}

export const getTranslations = ({ context, profile, relParents }: TGetTranslations) =>
  new Promise((resolve, reject) => {
    // hacer la query a pageName y si loadGeneric? también las comunes
    // una query compuesta que nos permita pedir ambas o una
    // sería lo mejor
    const translationsURL = `${I18N_MULTIPLE_ROUTE}?profile=${profile}&parentId=${relParents.join(',')}`;
    const headers = prepareHeadersFromContext(context);

    get({ path: translationsURL, headers })
      .then(({ data }) => {
        const { status, labels } = data;
        if (status !== 'OK') {
          reject(status);
        } else {
          resolve(labels);
        }
      })
      .catch(reject);
    // Resolver en base a loadGeneric
  });

export const getSingleTranslation = (profile: string, rel_parent: string, rel_anchor: string) =>
  new Promise((resolve, reject) => {
    const translationsURL = `${I18N_GET_SINGLE}?profile=${profile}&parentId=${rel_parent}&anchorId=${rel_anchor}`;

    get({ path: translationsURL })
      .then(({ data }) => {
        const { status, label } = data;

        if (status !== 'OK') {
          reject(data);
        } else {
          resolve(label.value);
        }
      })
      .catch(reject);
  });

export const checkProfile = ({ context, profile, filters, gender, page, material }: TCheckProfile) =>
  new Promise<TProfileResponse>((resolve, reject) => {
    let url = `${CHECK_PROFILE_ROUTE}?profile=${profile}`;

    const params = { gender, page, material };
    const hasParams = Object.keys(params).some((paramKey) => params[paramKey] !== undefined);

    if (hasParams) {
      Object.keys(params).forEach((paramKey) => {
        url = `${url}&${paramKey}=${params[paramKey]}`;
      });
    }

    if (filters) {
      url = `${url}&${filters}`;
    }

    const headers = prepareHeadersFromContext(context);
    get({ path: url, headers })
      .then((response) => {
        if (response && response.data) {
          const { data } = response;

          if (data) {
            if (data.audit) delete data.audit;
            if (data.status) delete data.status;
          }
          resolve(data as TProfileResponse);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });

export const getCountries = ({ context, profile }: TGeneralActionType) =>
  new Promise((resolve, reject) => {
    const headers = prepareHeadersFromContext(context);

    get({ path: `${COUNTRIES_ROUTE}?profile=${profile}`, headers })
      .then((response) => {
        if (response) {
          const { data } = response;
          if (data && data.status === 'OK') {
            resolve(data.countries);
          }
        }
      })
      .catch(reject);
  });

export const getPaymentMethods = ({ context, lang, countryId }: TGetMethods) =>
  new Promise((resolve, reject) => {
    const path = `${PAYMENT_METHODS_GET_ROUTE}?profile=${lang}&countryId=${countryId}`;
    const headers = prepareHeadersFromContext(context);

    get({ path, headers })
      .then((response) => {
        if ('data' in response) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });

export const getShippingMethods = ({ context, lang, countryId }: TGetMethods) =>
  new Promise((resolve, reject) => {
    const path = `${SHIPPING_METHODS_GET_ROUTE}?profile=${lang}&countryId=${countryId}`;
    const headers = prepareHeadersFromContext(context);

    get({ path, headers })
      .then((response) => {
        if ('data' in response) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });

export const subscribeTwsEvent = (data: TSubscribeTwsEvent) =>
  new Promise((resolve, reject) => {
    post({
      path: TWS_EVENT_SUBSCRIBE,
      data: {
        ...data,
      },
    })
      .then((response) => {
        if (response && response.data && response.data.status === 'OK') {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });

export const unsubscribeTwsEvent = (data: TTwsEvent) =>
  new Promise((resolve, reject) => {
    post({
      path: TWS_EVENT_UNSUBSCRIBE,
      data: {
        ...data,
      },
    })
      .then((response) => {
        if (response && response.data && response.data.status === 'OK') {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });

/**
 * @typedef {Object} TranslationsObject
 * @property {string} share
 * @property {string} title
 * @property {string} titleNotMember
 * @property {string} subtitle
 * @property {string} subtitleNotMember
 * @property {string} extraInfo
 * @property {string} subscribe
 * @property {string} unsubscribe
 * @property {string} unsubscribeConfirm
 * @property {string} unsubscribeConfirmText
 */

/**
 * @param {Object} data
 * @param {string} data.profile
 * @param {string} data.countryId
 *
 * @returns {Object} response
 * @returns {string} response.urlTc
 * @returns {TranslationsObject} response.translations
 */

type TConditionsTwsEvent = {
  profile: string;
  countryId: string;
};

type TConditionsTwsEventResponse = {
  urlTc: string;
  translations: {
    share: string;
    title: string;
    titleNotMember: string;
    subtitle: string;
    subtitleNotMember: string;
    extraInfo: string;
    subscribe: string;
    unsubscribe: string;
    unsubscribeConfirm: string;
    unsubscribeConfirmText: string;
  };
};

export const conditionsTwsEvent = (data: TConditionsTwsEvent) =>
  new Promise<TConditionsTwsEventResponse>((resolve, reject) => {
    const searchParams = new URLSearchParams(Object.entries(data));
    get({
      path: `${TWS_EVENT_CONDITIONS}?${searchParams.toString()}`,
    })
      .then((response) => {
        if (response && response.data && response.data.status === 'OK') {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });

export type TNewsletter = {
  email: string;
  profile:
    | string
    | {
        countryId: string;
        languageId: string;
      };
  origin: string;
  ewallet?: boolean;
  campaign?: string;
  lab?: boolean;
  telephone?: string;
  phonePrefix?: string;

  birthDate?: any;

  gender?: string;

  countryId?: string;
  languageId?: string;
};

export const subscribeNewsletter = (data: TNewsletter) =>
  new Promise((resolve, reject) => {
    const originFields = handleOriginByCookie();
    const utmParams = getUtmParams({ campaign: data.campaign });
    const lab = getDomainValue({ camper: data.lab === true, nnormal: false, camperlab: true });
    post({
      path: NEWSLETTER_SUBSCRIBE,
      data: {
        ...data,
        ...originFields,
        ...utmParams,
        lab,
      },
    })
      .then((response) => {
        if (response && response.data && response.data.status === 'OK') {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch((error) => {
        console.error(error);
        return error;
      });
  });

export const updateNewsletter = (data: TNewsletter) =>
  new Promise((resolve, reject) => {
    post({
      path: NEWSLETTER_UPDATE,
      data,
    })
      .then((response) => {
        if (response && response.data && response.data.status === 'OK') {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });

type TUnsubscribeNewsletter = {
  email: string;
  profile: string;
  reason?: string;
  unsubscribeOrigin?: string;
  unsubscribeCampaign?: string;
  unsubscribeChannel?: string;
  usrhash?: string;
  signature?: string;
  launchListId?: string;
  emailId?: string;
  contactUid?: string;
};

export const unsubscribeNewsletter = (data: TUnsubscribeNewsletter) =>
  new Promise((resolve, reject) => {
    post({
      path: NEWSLETTER_UNSUBSCRIBE,
      data: {
        ...data,
      },
    })
      .then((response) => {
        if (response && response.data && response.data.status === 'OK') {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });

export const getFAQs = ({ context, lang }: { context?: GetServerSidePropsContext; lang: string }) =>
  new Promise((resolve, reject) => {
    const path = `${FAQS}?profile=${lang}`;
    const headers = prepareHeadersFromContext(context);

    get({ path, headers })
      .then((response) => {
        if ('data' in response) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });

export const getInfo = ({ context, profile }: TGeneralActionType) =>
  new Promise((resolve, reject) => {
    const headers = prepareHeadersFromContext(context);

    get({ path: `${NEWSLETTER_INFO_ROUTE}?profile=${profile}`, headers })
      .then((response) => {
        if (response && 'data' in response) {
          const { data } = response;

          if (data) {
            resolve(data);
          } else {
            reject(data);
          }
        }
      })
      .catch(reject);
  });

export const getSeo = (context: any, queryparams: string[] = []) =>
  new Promise((resolve, reject) => {
    const headers = prepareHeadersFromContext(context);

    get({ path: `${SEO}?${queryparams.join('&')}`, headers })
      .then((response) => {
        if (response) {
          const { data } = response;
          if (data && data.status === 'OK') {
            const { metaKeywords, metaDescription, revision, titlePage: pageTitle } = data;
            resolve({
              metaKeywords: metaKeywords || null,
              metaDescription,
              revision,
              pageTitle,
            });
          } else {
            reject(data);
          }
        }
      })
      .catch(reject);
  });

/**
 * Este método, como solo se usa en Cliente, lo vamos a limitar a entornos local e integra.
 * No usar nunca en Cliente en Producción. Si se necesitara en Producción ha de ser de forma teporal,
 * quitando / poniendo un "return;" al principio del método
 */

type TApiLog = {
  profile: string;
  objectNode?: any;
  message: string;
  environment?: string;
};
export const apiLog = ({ profile, objectNode, message }: TApiLog) =>
  new Promise((resolve, reject) => {
    const obj = objectNode !== undefined && objectNode !== null ? objectNode : {};
    if (message) {
      obj.message = message;
    }

    post({
      path: LOG_ROUTE,
      data: {
        profile,
        objectNode: obj,
      },
    })
      .then((response) => {
        if ('data' in response) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });

/**
 * Este método, como solo se usa en Cliente, lo vamos a limitar a entornos local e integra.
 * No usar nunca en Cliente en Producción. Si se necesitara en Producción ha de ser ServerSide,
 * por lo que habría que modificar el if añadiendo por ej un parámetro adicional a la llamada.
 */
export const apiLogPrivate = ({ environment, profile, objectNode, message }: TApiLog) =>
  new Promise((resolve, reject) => {
    if (environment === 'prod') {
      return;
    }

    const obj = objectNode !== undefined && objectNode !== null ? objectNode : {};
    if (message) {
      obj.message = message;
    }

    const base = getPrivateUrl(environment);

    post({
      base,
      path: LOG_ROUTE,
      data: {
        profile,
        objectNode: obj,
      },
    })
      .then((response) => {
        if ('data' in response) {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });
type TRegionsAndCitiesResponse = {
  regions?: { id: string; name: string }[];
  cities?: string[];
};
export const getRegionsAndCities = (locale: string) =>
  new Promise<ApiOK<TRegionsAndCitiesResponse>>((resolve, reject) => {
    get({ path: `${REGIONS_AND_CITIES}?profile=${locale}` })
      .then((response) => {
        if (response) {
          const { data } = response;
          if (data && data.status === 'OK') {
            resolve(data);
          }
        }
      })
      .catch(reject);
  });

type TNewsletterPreferencesBody = {
  profile: string;
  categories?: Record<string, unknown>[];
  preferences?: Record<string, unknown>[];
  email: string;
  usrhash?: string;
  signature?: string;
};

export const changeNewsletterPreferences = (body: TNewsletterPreferencesBody) =>
  new Promise((resolve, reject) => {
    post({
      path: NEWSLETTER_PREFERENCES,
      data: body,
    })
      .then((response) => {
        if (response) {
          const { data } = response;

          if (data && data.status === 'OK') {
            resolve(data);
          }
        }
      })
      .catch(reject);
  });

export const getNewsletterPreferences = (body: TNewsletterPreferencesBody) =>
  new Promise((resolve, reject) => {
    post({
      path: GET_NEWSLETTER_PREFERENCES,
      data: body,
    })
      .then((response) => {
        if (response) {
          const { data } = response;

          if (data && data.status === 'OK') {
            resolve(data);
          }
          reject(data);
        }
      })
      .catch(reject);
  });

export const getIsSelligent = (locale: string) =>
  new Promise((resolve, reject) => {
    get({ path: `${SELLIGENT_POPUP}?profile=${locale}` })
      .then((response) => {
        if (response) {
          const { data } = response;
          if (data && data.status === 'OK' && data.url) {
            resolve(data.url);
          } else {
            resolve(false);
          }
        }
      })
      .catch(reject);
  });

export const getDonationActive = (data: TGeneralActionType) =>
  new Promise((resolve, reject) => {
    post({
      path: DONATION_BEGIN,
      data: {
        ...data,
      },
    })
      .then((response) => {
        if (response && response.data && response.data.status === 'OK') {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });

type TMakeDonation = {
  profile: string;
  customerReference: string;
  email: string;
  reference: string;
  paymentMethod: string;
  [key: string]: any;
};

export const makeDonation = (data: TMakeDonation) =>
  new Promise((resolve, reject) => {
    post({
      path: DONATION_DONATE,
      data: {
        ...data,
      },
    })
      .then((response) => {
        if (response && response.data && response.data.status === 'OK') {
          resolve(response.data);
        } else {
          reject(response);
        }
      })
      .catch(reject);
  });

export const getI18nTimeStamp = (i18nString: string[]) =>
  new Promise((resolve, reject) => {
    if (process.env.ENVIRONMENT === 'local') return;
    const base = process.env.MISC_ENDPOINT || getDomainValue({ camper: 'https://misc.camper.com', nnormal: 'https://misc.nnormal.com' });
    console.log('[i18n] Missing labels:', i18nString);

    browserToSHA256(i18nString.join()).then((hash) => {
      const path = `${I18N_LABELS}${hash}`;

      get({
        base,
        path,
      })
        .then((response) => {
          let shouldReject = false;
          let returnValue = null;

          if (response) {
            const { headers } = response;

            if (headers && headers['x-cache']) {
              const cache = headers['x-cache'];

              returnValue = cache === CLOUDFRONT_MISS;
            } else {
              shouldReject = true;
            }
          } else {
            shouldReject = true;
          }

          if (shouldReject) {
            reject(response);
          } else {
            resolve({
              hash,
              shouldPost: returnValue,
            });
          }
        })
        .catch(reject);
    });
  });
/**
 * @returns {Array.<{id: String, name: String, priceFormatted: String, infoList?: Array<string>}>}
 */
export async function getGlobalReturnMethods(locale: string, countryId: string) {
  if (!locale) {
    throw new Error('missing locale');
  }
  const r = await get({ base: process.env.API_GATEWAY, path: `${GLOBAL_RETURN_METHODS}?profile=${locale}&countryId=${countryId}` });
  return r?.data?.returnMethods ?? [];
}

interface TGetTranslationsNew extends TGeneralActionType {
  page: string;
}

export async function getTranslationsNew({ context, profile, page }: TGetTranslationsNew) {
  const headers = prepareHeadersFromContext(context);
  const path = `${I18N_NEW}?profile=${profile}&page=${page}`;

  const result = await get({ path, headers });

  if (result && result?.data && result?.data?.labels) {
    return result.data.labels;
  }

  return result;
}

export async function getComunas({ context, profile }: TGeneralActionType) {
  const path = `${CHILE_COMUNAS}?profile=${profile}`;
  try {
    const headers = prepareHeadersFromContext(context);

    const result = await get({ path, headers });

    if (result.data.status === 'OK' && Array.isArray(result?.data?.comunas)) {
      return result.data.comunas;
    }
    throw result;
  } catch (error) {
    logError(String({ path, error }));
    return [];
  }
}

interface TEnrichTwsEvent extends TGeneralActionType {
  payload: Record<string, any>;
}

export async function enrichTwsEvent({ context, profile, payload }: TEnrichTwsEvent) {
  const path = `${ENRICHMENT_TWS}`;
  try {
    const headers = prepareHeadersFromContext(context);

    const result = await post({
      path,
      headers,
      data: {
        profile,
        ...payload,
      },
    });

    if (result.data.status === 'OK') {
      return true;
    }

    throw result;
  } catch (error) {
    logError(String({ path, error }));
    return false;
  }
}

type TDebugAPI = {
  profile: string;
  url: string;
  payload: Record<string, any>;
  response: Record<string, any>;
};

export async function debugAPI({ profile, url, payload, response }: TDebugAPI) {
  try {
    const result = await post({
      path: DEBUG,
      data: {
        profile,
        url,
        payload,
        response,
      },
    });

    console.log('API DEBUG RESULT', result);

    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
}
